import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiConfigs } from "../../constants/apiConfigs";
import { isFresh } from "../../utils/helpers";
import { ERROR, errors } from "../../constants/errors";
import { fetchDMAutomation as fetchDMAutomationAPI } from "../../services/api/dmAutomations";
import { DMAutomationItem } from "../../types/automations/dmAutomationTypes";

export interface DMAutomationSlice {
    loading: boolean,
    items: Record<string, {dmAutomation: DMAutomationItem, last_fetched_at: number}>
    error: string|null,
    error_code: number | null
}

export const initialState: DMAutomationSlice = {
    loading: false,
    items: {},
    error: null,
    error_code: null,
}

const STALE_TIME = apiConfigs.FETCH_INFLUENCER_POST.staleTime;


export const fetchDMAutomation = createAsyncThunk(
    'content/fetchDMAutomation',
    async ({postId, force= false}: {postId: string, force?: boolean}, { getState, rejectWithValue }) => {
        const state = getState() as { dmAutomations: DMAutomationSlice };
        if (!force && postId in state.dmAutomations.items &&
            isFresh(state.dmAutomations.items[postId].last_fetched_at, STALE_TIME)
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const dmAutomation = await fetchDMAutomationAPI(postId);
            return { dmAutomation, postId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

const dmAutomationsSlice = createSlice({
    name: 'dmAutomations',
    initialState,
    reducers: {
        markExpired: (state, data) => {
            const postId = data.payload;
            if(postId && postId in state.items){
                state.items[postId] = {...state.items[postId]};
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDMAutomation.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.error_code = null
            })
            .addCase(fetchDMAutomation.fulfilled, (state, action) => {
                state.items[action.payload.postId] = { ...action.payload, last_fetched_at: Date.now()}
                state.loading = false;
            })
            .addCase(fetchDMAutomation.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    switch (action.payload) {
                        case ERROR.NOT_FOUND:
                        default:
                            state.error_code = errors[ERROR.NOT_FOUND].code;
                            state.error = errors[ERROR.NOT_FOUND].message;
                    }
                }
            })
    }
})

export const { markExpired } = dmAutomationsSlice.actions;


export default dmAutomationsSlice.reducer
